import axios from "axios";
import { getAuthApiRoute } from "@/helpers/api.util";
import {
  clearAuthTokens,
  getAuthTokens,
  setAuthTokens,
} from "@/helpers/auth/tokenStorage";

const axiosInstance = axios.create({});

axiosInstance.interceptors.request.use(
  (request) => {
    try {
      const { accessToken } = getAuthTokens();

      // Temporary login tokens and other explicit credentials must win over
      // the persisted session token.
      if (accessToken && !request.skipAuth && !request.headers.Authorization) {
        request.headers["Authorization"] = `Bearer ${accessToken}`;
      }

      return request;
    } catch (error) {
      console.log("ERROR on Intercepting Access Token" + error);
      return request;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

const refreshPromises = {
  client: null,
  admin: null,
};

const silentLogout = () => {
  try {
    clearAuthTokens();

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
  } catch {
    // Auth cleanup must never mask the original API error.
  }
};

const isTerminalRefreshError = (error) =>
  [400, 401, 403, 422].includes(error?.response?.status);

const refreshSession = async (sessionType) => {
  const { refreshToken } = getAuthTokens();

  if (!refreshToken) {
    silentLogout();
    throw new Error("No refresh token is available");
  }

  try {
    // Plain axios avoids recursively entering this interceptor.
    const refreshPath =
      sessionType === "admin" ? "admin/refresh-token" : "refresh-token";
    const response = await axios.post(getAuthApiRoute(refreshPath), {
      refreshToken,
    });
    const tokens = response.data?.data ?? response.data;

    if (!tokens?.accessToken) {
      const invalidResponseError = new Error(
        "The refresh endpoint did not return an access token"
      );
      invalidResponseError.response = response;
      throw invalidResponseError;
    }

    // V2 rotates refresh tokens. Persist both before retrying queued requests.
    // Retain the old refresh token for backward-compatible responses.
    setAuthTokens(tokens.accessToken, tokens.refreshToken || refreshToken, {
      sessionType,
    });
    return tokens.accessToken;
  } catch (error) {
    // Authentication failures are terminal. Network failures, rate limits and
    // 5xx responses are transient and must not destroy a recoverable session.
    if (isTerminalRefreshError(error)) silentLogout();
    throw error;
  }
};

export const refreshAuthSession = (requestedSessionType) => {
  const storedSessionType = getAuthTokens().sessionType;
  const sessionType =
    requestedSessionType === "admin" || storedSessionType === "admin"
      ? "admin"
      : "client";

  if (!refreshPromises[sessionType]) {
    refreshPromises[sessionType] = refreshSession(sessionType).finally(() => {
      refreshPromises[sessionType] = null;
    });
  }

  return refreshPromises[sessionType];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.skipAuthRefresh
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const sessionType =
      originalRequest.authSessionType === "admin" ? "admin" : undefined;

    try {
      const newAccessToken = await refreshAuthSession(sessionType);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
